import React, { useMemo } from 'react';
import { useDeleteServiceableUrlMutation, useFetchServiceableUrlsForClusterQuery } from './service';
import LoaderCard from '../shared/LoaderCard';
import { Card } from 'antd';
import ServiceableUrlsTable from './table/ServiceableUrlsTable';
import useAddUrlAnalysisProcessWithNotification from '../process/hooks/useAddUrlAnalysisProcessWithNotification';
import useBatchUrlAnalysisProcessesPolling from '../process/hooks/useBatchUrlAnalysisProcessesPolling';
import { SERVICEABLE_URLS_POLLING_TIME_MS } from '../settings';
import ScoreIndicator from '../shared/ScoreIndicator';
import { ServiceableUrl } from './types';
import { useFetchUrlClustersQuery } from '../urlClusters/service';
import AddNewServiceableUrlsButton from './AddNewServiceableUrlsButton';

function getAverageUrlSeoScore(urls: ServiceableUrl[]) {
    return (
        urls.reduce((sum, url) => {
            return sum + (url.on_page_seo?.score || 0);
        }, 0) / urls.length
    );
}

type Props = {
    websiteGuid: string;
    clusterGuid: string;
};

export default function ClusterUrls({ websiteGuid, clusterGuid }: Props) {
    const { isRunningBatchAnalysis } = useBatchUrlAnalysisProcessesPolling(websiteGuid);
    const { data: urlClusters, isLoading: isLoadingUrlClusters } = useFetchUrlClustersQuery({ w_guid: websiteGuid });
    const { data: clusterUrls, isLoading: isLoadingUrls } = useFetchServiceableUrlsForClusterQuery(
        {
            w_guid: websiteGuid,
            cluster_guid: clusterGuid,
        }, // If we are running an analysis, refetch if the data is old
        { refetchOnMountOrArgChange: isRunningBatchAnalysis ? SERVICEABLE_URLS_POLLING_TIME_MS / 1000 : false }
    );
    const [deleteServiceableUrl, { isLoading: isDeletingUrl }] = useDeleteServiceableUrlMutation();

    const { addUrlAnalysisProcess, isAddingUrlAnalysisProcess } = useAddUrlAnalysisProcessWithNotification();

    const handleCheckUrlClick = (urlGuid: string) => {
        addUrlAnalysisProcess(websiteGuid, urlGuid);
    };

    const handleDeleteUrlClick = (urlGuid: string) => {
        deleteServiceableUrl({ w_guid: websiteGuid, url_guid: urlGuid });
    };

    const averageSeoScore = useMemo(() => getAverageUrlSeoScore(clusterUrls || []), [clusterUrls]);
    const isLoading = isLoadingUrls || isLoadingUrlClusters;
    return (
        <>
            {isLoading && <LoaderCard />}
            {!isLoading && (
                <Card
                    title={
                        <div
                            className="page-group-seo-score"
                            style={{ display: 'flex', gap: '12px' }}
                        >
                            <span>Avg. SEO Score:</span>
                            <ScoreIndicator
                                value={averageSeoScore}
                                showDecimals
                            />
                        </div>
                    }
                    extra={
                        <AddNewServiceableUrlsButton
                            websiteGuid={websiteGuid}
                            clusterGuid={clusterGuid}
                        />
                    }
                >
                    <ServiceableUrlsTable
                        serviceableUrls={clusterUrls || []}
                        urlClusters={urlClusters || []}
                        isCheckButtonLoading={isAddingUrlAnalysisProcess}
                        isDeleteButtonLoading={isDeletingUrl}
                        onCheck={handleCheckUrlClick}
                        onDelete={handleDeleteUrlClick}
                    />
                </Card>
            )}
        </>
    );
}
